// ─── Value formatting ────────────────────────────────────────────────────────

// Tenderbook sends dates as either epoch millis or an already-formatted string, depending on the
// operation. Timestamps/ISO strings get formatted; anything else is shown as the backend sent it.
export const formatDate = (value) => {
	if (!value) return '-'

	const isTimestamp = typeof value === 'number' || /^\d+$/.test(String(value))
	const isIso = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)
	if (!isTimestamp && !isIso) return String(value)

	const date = new Date(isTimestamp ? Number(value) : value)
	if (Number.isNaN(date.getTime())) return '-'

	return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

export const formatDateTime = (value) => {
	if (!value) return '-'
	const date = new Date(Number(value) || value)
	if (Number.isNaN(date.getTime())) return '-'
	return date.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export const formatValue = (value) => {
	if (value === null || value === undefined || value === '') return '-'
	if (Array.isArray(value)) return value.length ? value.join(', ') : '-'
	return value
}

export const yesNo = (value) => (value ? 'Yes' : 'No')

// Compact Indian-format amount: 1234567 → ₹12.35L.
export const formatAmount = (value) => {
	const amount = Number(value)
	if (!Number.isFinite(amount)) return '-'
	if (Math.abs(amount) >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`
	if (Math.abs(amount) >= 100000) return `₹${(amount / 100000).toFixed(2)}L`
	if (Math.abs(amount) >= 1000) return `₹${(amount / 1000).toFixed(2)}K`
	return `₹${amount.toLocaleString('en-IN')}`
}

// Milliseconds → the largest sensible unit, e.g. 2757056 → "45.9 min".
export const formatDuration = (ms) => {
	const value = Number(ms)
	if (!Number.isFinite(value) || value <= 0) return '-'
	const seconds = value / 1000
	if (seconds < 60) return `${seconds.toFixed(0)} sec`
	const minutes = seconds / 60
	if (minutes < 60) return `${minutes.toFixed(1)} min`
	const hours = minutes / 60
	if (hours < 24) return `${hours.toFixed(1)} hrs`
	return `${(hours / 24).toFixed(1)} days`
}

// ─── Chart data shaping ──────────────────────────────────────────────────────

// Turns the API's [{ value, count }] breakdowns into recharts rows, largest first.
// Returns [] for the null breakdowns so callers can fall back to an empty state.
export const toChartData = (list, limit = 8) => {
	if (!Array.isArray(list)) return []
	return list
		.filter((item) => item?.value !== null && item?.value !== undefined)
		.map((item) => ({ name: titleCase(String(item.value)), count: Number(item.count) || 0 }))
		.sort((a, b) => b.count - a.count)
		.slice(0, limit)
}

// activity_tracker is an object keyed by event name — flatten it into chart rows.
export const activityToChartData = (tracker) => {
	if (!tracker || typeof tracker !== 'object') return []
	return Object.entries(tracker)
		.map(([key, event]) => ({
			name: titleCase(key.replace(/_/g, ' ')),
			count: Number(event?.count) || 0,
			firstAt: event?.first_at,
			lastAt: event?.last_at,
		}))
		.sort((a, b) => b.count - a.count)
}

export const titleCase = (value) =>
	String(value ?? '')
		.split(' ')
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(' ')

// ─── Tender lists ────────────────────────────────────────────────────────────

// The tender lists come back either as bare tender objects or wrapped — { tender, tender_result,
// followed_at } — so unwrap whichever. `key` picks the side to read: the result lists carry their
// payload under `tender_result` and leave `tender` null.
export const toTenderList = (list, key = 'tender') => {
	if (!Array.isArray(list)) return []
	return list.map((entry) => (entry && key in entry ? entry[key] : entry)).filter((tender) => tender && typeof tender === 'object')
}

// companies_viewed wraps the company under `competitors`, alongside when it was viewed.
export const toCompanyList = (list) => {
	if (!Array.isArray(list)) return []
	return list
		.map((entry) => (entry?.competitors ? { ...entry.competitors, viewed_at: entry.created_at } : entry))
		.filter((company) => company && typeof company === 'object')
}
